import type {
    ClinicData,
    Consultation,
    FollowUp,
    Gender,
    Medicine,
    Patient,
    Prescription,
    ReminderActivity,
    ReminderChannel
} from '@/lib/clinic/types'

import { createAudit } from '@/lib/clinic/audit'
import { describeComplaint } from '@/lib/clinic/format'
import { formatPatientCode, formatPrescriptionNumber } from '@/lib/clinic/ids'
import { fillTemplate } from '@/lib/clinic/whatsapp'

import {
    addDays,
    combineDateTime,
    formatDate,
    formatDateTime,
    toDayKey
} from '@/lib/clinic/datetime'

import { COMMON_MEDICINES, DEFAULT_SETTINGS } from '@/constants/clinic'

type CaseKey =
    | 'rctStart'
    | 'rctComplete'
    | 'crownPrep'
    | 'crownFit'
    | 'scaling'
    | 'checkup'
    | 'extraction'
    | 'extractionReview'
    | 'filling'
    | 'sensitivity'
    | 'periodontitis'
    | 'periodontitisUpper'
    | 'whitening'
    | 'whiteningFinal'
    | 'dentureStart'
    | 'dentureDelivery'
    | 'dentureReview'
    | 'bridgePrep'
    | 'ulcer'
    | 'childCaries'
    | 'brokenTooth'
    | 'wisdomPain'

interface CaseTemplate {
    complaints: string[]
    complaintNote: string
    findings: string
    diagnosis: string
    treatments: string[]
    treatmentNote: string
    notes: string
    medicines: string[]
    advice: string
    followUpTreatment: string
    followUpNotes: string
}

interface ReminderSeed {
    day: number
    time: string
    channel: ReminderChannel
}

interface FollowUpSeed {
    inDays: number
    completed?: boolean
    completedAfter?: number
    notes?: string
    reminders?: ReminderSeed[]
}

interface VisitSeed {
    day: number
    time: string
    case: CaseKey
    tooth?: string
    medicines?: string[]
    corrected?: boolean
    followUp?: FollowUpSeed
}

interface PatientSeed {
    name: string
    gender: Gender
    age?: number
    dateOfBirth?: string
    phone: string
    place: string
    address?: string
    medicalHistory?: string
    allergies?: string
    notes?: string
    registered?: { day: number, time: string }
    visits: VisitSeed[]
}

const CASES: Record<CaseKey, CaseTemplate> = {
    rctStart: {
        complaints: ['Tooth pain'],
        complaintNote: 'Severe pain in {side} back tooth for 3 days, worse at night',
        findings: 'Deep carious lesion i.r.t {t}. Tender on vertical percussion. Lingering pain on cold test. IOPA shows caries approaching the pulp.',
        diagnosis: 'Symptomatic irreversible pulpitis i.r.t {t}',
        treatments: ['Root Canal Treatment'],
        treatmentNote: 'Access opening and pulp extirpation done i.r.t {t}. Calcium hydroxide dressing and temporary filling placed.',
        notes: 'RCT planned in two sittings followed by a crown. Procedure explained to the patient.',
        medicines: ['Amoxicillin 500 mg', 'Aceclofenac 100 mg + Paracetamol 325 mg', 'Pantoprazole 40 mg'],
        advice: 'Avoid chewing on the treated side until the root canal treatment is complete.\nDo not eat for one hour after the temporary filling.\nComplete the full course of antibiotics.',
        followUpTreatment: 'Root Canal Treatment i.r.t {t}',
        followUpNotes: 'RCT second sitting: cleaning, shaping and obturation'
    },
    rctComplete: {
        complaints: ['RCT follow-up visit'],
        complaintNote: 'No pain since the last visit',
        findings: 'Tooth {t} asymptomatic. Temporary restoration intact. No tenderness on percussion.',
        diagnosis: 'Root canal treatment in progress i.r.t {t}',
        treatments: ['Root Canal Treatment'],
        treatmentNote: 'Cleaning and shaping completed, obturation done i.r.t {t}. Post-obturation IOPA satisfactory.',
        notes: 'Crown advised to protect the root canal treated tooth.',
        medicines: ['Ibuprofen 400 mg'],
        advice: 'Mild discomfort for 2 to 3 days is normal.\nAvoid biting hard food on this tooth until the crown is placed.',
        followUpTreatment: 'Root Canal Treatment i.r.t {t}',
        followUpNotes: 'Crown preparation and impression'
    },
    crownPrep: {
        complaints: ['Crown appointment'],
        complaintNote: 'Root canal treated tooth, came for a crown',
        findings: 'Endodontically treated {t} with satisfactory obturation. Remaining tooth structure weak.',
        diagnosis: 'Endodontically treated tooth {t} requiring a full coverage crown',
        treatments: ['Dental Crown'],
        treatmentNote: 'Tooth preparation done i.r.t {t}. Impression taken for PFM crown, shade A2. Temporary crown cemented.',
        notes: 'Crown expected from the lab in 7 days.',
        medicines: [],
        advice: '',
        followUpTreatment: 'Dental Crown i.r.t {t}',
        followUpNotes: 'Crown trial and cementation'
    },
    crownFit: {
        complaints: ['Crown fixing'],
        complaintNote: '',
        findings: 'Temporary crown intact. Gingiva healthy around {t}.',
        diagnosis: 'Prepared tooth {t} ready for crown cementation',
        treatments: ['Dental Crown'],
        treatmentNote: 'PFM crown tried in, bite and contacts checked, cemented with GIC i.r.t {t}.',
        notes: 'Six-month review advised.',
        medicines: [],
        advice: '',
        followUpTreatment: 'Dental Crown i.r.t {t}',
        followUpNotes: 'Six-month review of crown and oral hygiene'
    },
    scaling: {
        complaints: ['Gum bleeding', 'Bad breath'],
        complaintNote: 'Bleeding while brushing for 2 months',
        findings: 'Generalised plaque and calculus deposits. Gingiva red and swollen with bleeding on probing. No mobility.',
        diagnosis: 'Chronic generalised gingivitis',
        treatments: ['Teeth Cleaning / Scaling'],
        treatmentNote: 'Full mouth ultrasonic scaling and polishing done.',
        notes: 'Oral hygiene instructions given. Advised a soft toothbrush and daily flossing.',
        medicines: ['Chlorhexidine 0.2% Mouthwash'],
        advice: 'Use the mouthwash 30 minutes after brushing.\nSlight sensitivity after cleaning is normal and settles in a few days.\nBrush twice daily and floss once daily.',
        followUpTreatment: 'Teeth Cleaning / Scaling',
        followUpNotes: 'Routine checkup and cleaning'
    },
    checkup: {
        complaints: ['Routine dental checkup'],
        complaintNote: '',
        findings: 'Oral hygiene fair. Mild extrinsic stains on lower front teeth. No active caries.',
        diagnosis: 'Healthy dentition with extrinsic stains',
        treatments: ['Dental Checkup', 'Teeth Cleaning / Scaling'],
        treatmentNote: 'Oral prophylaxis and polishing done.',
        notes: 'Six-month recall advised.',
        medicines: [],
        advice: '',
        followUpTreatment: 'Dental Checkup',
        followUpNotes: 'Six-month routine checkup'
    },
    extraction: {
        complaints: ['Tooth pain', 'Swelling'],
        complaintNote: 'Pain and swelling near {side} back tooth for 5 days',
        findings: 'Grossly decayed {t} with mild buccal swelling. No fever. Tooth not restorable.',
        diagnosis: 'Grossly decayed non-restorable {t} with chronic periapical abscess',
        treatments: ['Tooth Extraction'],
        treatmentNote: 'Extraction of {t} done under local anaesthesia. Bleeding controlled, 2 sutures placed.',
        notes: 'Post-extraction instructions explained. Replacement options discussed.',
        medicines: ['Amoxicillin + Clavulanic Acid 625 mg', 'Metronidazole 400 mg', 'Aceclofenac 100 mg + Paracetamol 325 mg', 'Pantoprazole 40 mg'],
        advice: 'Bite on the cotton pack for 45 minutes.\nDo not spit, rinse or use a straw for 24 hours.\nHave cold, soft food today and avoid hot drinks.\nApply an ice pack on the cheek for 10 minutes at a time if swelling occurs.',
        followUpTreatment: 'Tooth Extraction i.r.t {t}',
        followUpNotes: 'Suture removal and healing review'
    },
    extractionReview: {
        complaints: ['Suture removal'],
        complaintNote: 'No pain, mild discomfort while eating',
        findings: 'Extraction socket of {t} healing well. Sutures intact, no signs of infection.',
        diagnosis: 'Healing extraction socket i.r.t {t}',
        treatments: ['Dental Checkup'],
        treatmentNote: 'Sutures removed and socket irrigated with saline.',
        notes: 'Bridge or implant advised to replace {t}.',
        medicines: [],
        advice: '',
        followUpTreatment: 'Replacement of {t}',
        followUpNotes: 'Discuss bridge or implant'
    },
    filling: {
        complaints: ['Tooth decay', 'Tooth sensitivity'],
        complaintNote: 'Food lodgement and sensitivity to sweets',
        findings: 'Occlusal caries i.r.t {t}. No tenderness on percussion. Pulp vitality normal.',
        diagnosis: 'Dental caries (Class I) i.r.t {t}',
        treatments: ['Dental Filling'],
        treatmentNote: 'Caries removed and composite restoration done i.r.t {t}.',
        notes: '',
        medicines: ['Potassium Nitrate 5% Toothpaste'],
        advice: 'Avoid very hot or cold food for 24 hours.\nCut down on sugary snacks between meals.',
        followUpTreatment: 'Dental Filling i.r.t {t}',
        followUpNotes: 'Review restoration'
    },
    sensitivity: {
        complaints: ['Tooth sensitivity'],
        complaintNote: 'Sharp pain with cold water for 1 month',
        findings: 'Cervical abrasion i.r.t 13, 14, 23, 24. Sensitive to cold air. Horizontal brushing habit.',
        diagnosis: 'Dentinal hypersensitivity due to cervical abrasion',
        treatments: ['Dental Checkup'],
        treatmentNote: 'Desensitising varnish applied. Brushing technique corrected.',
        notes: 'GIC restoration of the abraded areas if sensitivity continues.',
        medicines: ['Potassium Nitrate 5% Toothpaste'],
        advice: 'Use a soft toothbrush with gentle circular strokes.\nAvoid acidic drinks and very cold food for 2 weeks.',
        followUpTreatment: 'Desensitisation',
        followUpNotes: 'Recheck sensitivity, GIC restoration if needed'
    },
    periodontitis: {
        complaints: ['Gum bleeding', 'Swelling'],
        complaintNote: 'Loose feeling in lower front teeth and bleeding gums',
        findings: 'Probing depth 5 to 6 mm i.r.t lower front teeth. Grade I mobility i.r.t 31, 41. Subgingival calculus present.',
        diagnosis: 'Chronic periodontitis (Stage II, Grade B)',
        treatments: ['Gum Treatment'],
        treatmentNote: 'Scaling and root planing done for the lower arch under local anaesthesia.',
        notes: 'Diabetic. Advised to keep blood sugar under control. Upper arch SRP in the next visit.',
        medicines: ['Chlorhexidine 0.2% Mouthwash', 'Metronidazole 400 mg'],
        advice: 'Use the mouthwash twice daily for 2 weeks.\nGums may feel tender for 2 to 3 days.\nKeep your blood sugar under control for better gum healing.',
        followUpTreatment: 'Gum Treatment (SRP)',
        followUpNotes: 'Scaling and root planing for the upper arch'
    },
    periodontitisUpper: {
        complaints: ['Gum treatment, second sitting'],
        complaintNote: '',
        findings: 'Lower arch healing well, bleeding reduced. Probing depth 4 to 5 mm i.r.t upper molars.',
        diagnosis: 'Chronic periodontitis (Stage II, Grade B)',
        treatments: ['Gum Treatment'],
        treatmentNote: 'Scaling and root planing done for the upper arch under local anaesthesia.',
        notes: 'Periodontal maintenance every 3 months advised.',
        medicines: ['Chlorhexidine 0.2% Mouthwash'],
        advice: 'Continue the mouthwash for 2 weeks.\nBrush gently along the gum line twice daily.',
        followUpTreatment: 'Gum Treatment (SRP)',
        followUpNotes: 'Periodontal maintenance review'
    },
    whitening: {
        complaints: ['Discoloured teeth'],
        complaintNote: 'Wants whiter teeth before a family wedding',
        findings: 'Generalised extrinsic stains with mild yellow discolouration. No caries or sensitivity.',
        diagnosis: 'Extrinsic and age-related tooth discolouration',
        treatments: ['Teeth Cleaning / Scaling', 'Teeth Whitening'],
        treatmentNote: 'Oral prophylaxis followed by in-office whitening, session 1. Shade improved from A3 to A2.',
        notes: 'Second session planned after 2 weeks.',
        medicines: ['Potassium Nitrate 5% Toothpaste'],
        advice: 'Avoid tea, coffee, turmeric and coloured drinks for 48 hours.\nMild sensitivity for a day or two is normal.',
        followUpTreatment: 'Teeth Whitening',
        followUpNotes: 'Whitening session 2 and shade check'
    },
    whiteningFinal: {
        complaints: ['Whitening session 2'],
        complaintNote: 'No sensitivity after the first session',
        findings: 'Shade A2 after the first session. Gingiva healthy.',
        diagnosis: 'Tooth discolouration, whitening in progress',
        treatments: ['Teeth Whitening'],
        treatmentNote: 'Second in-office whitening session done. Final shade A1.',
        notes: 'Touch-up advised after one year.',
        medicines: [],
        advice: '',
        followUpTreatment: 'Teeth Whitening',
        followUpNotes: 'Shade review'
    },
    dentureStart: {
        complaints: ['Missing tooth'],
        complaintNote: 'Difficulty in chewing, old denture loose',
        findings: 'Completely edentulous upper arch. Lower arch with 33, 34, 43, 44 present. Well-formed ridges.',
        diagnosis: 'Completely edentulous maxilla with partially edentulous mandible',
        treatments: ['Denture'],
        treatmentNote: 'Primary impressions taken for upper complete denture and lower partial denture.',
        notes: 'Blood pressure checked before the procedure.',
        medicines: [],
        advice: '',
        followUpTreatment: 'Denture',
        followUpNotes: 'Jaw relation and denture try-in'
    },
    dentureDelivery: {
        complaints: ['Denture trial and delivery'],
        complaintNote: '',
        findings: 'Wax try-in satisfactory. Appearance, speech and bite acceptable.',
        diagnosis: 'Completely edentulous maxilla with partially edentulous mandible',
        treatments: ['Denture'],
        treatmentNote: 'Upper complete denture and lower partial denture inserted. Care instructions given.',
        notes: 'Remove dentures at night and keep them in water.',
        medicines: [],
        advice: '',
        followUpTreatment: 'Denture',
        followUpNotes: 'Denture review for sore spots'
    },
    dentureReview: {
        complaints: ['Loose denture'],
        complaintNote: 'Upper denture slipping while eating',
        findings: 'Mild looseness of the upper denture. Small sore spot on the lower ridge.',
        diagnosis: 'Ill-fitting upper denture due to ridge resorption',
        treatments: ['Denture'],
        treatmentNote: 'Upper denture relined chairside and sore spot relieved.',
        notes: '',
        medicines: ['Choline Salicylate Oral Gel'],
        advice: 'Apply the gel on the sore area three times a day.\nRemove the dentures at night and clean them daily.',
        followUpTreatment: 'Denture relining',
        followUpNotes: 'Review denture fit and sore spot'
    },
    bridgePrep: {
        complaints: ['Missing tooth'],
        complaintNote: 'Gap after extraction, wants a fixed replacement',
        findings: 'Missing {t}. Adjacent teeth sound with good bone support.',
        diagnosis: 'Partially edentulous span i.r.t {t}',
        treatments: ['Dental Bridge'],
        treatmentNote: 'Abutment teeth prepared for a 3-unit bridge. Impression taken, temporary bridge placed.',
        notes: '',
        medicines: [],
        advice: '',
        followUpTreatment: 'Dental Bridge i.r.t {t}',
        followUpNotes: 'Bridge trial and cementation'
    },
    ulcer: {
        complaints: ['Mouth ulcer'],
        complaintNote: 'Painful ulcer on the lower lip for 4 days',
        findings: 'Single shallow ulcer of about 4 mm on the lower labial mucosa with a red halo.',
        diagnosis: 'Minor aphthous ulcer',
        treatments: ['Topical medication'],
        treatmentNote: '',
        notes: 'Review if not healed in 10 days.',
        medicines: ['Choline Salicylate Oral Gel', 'Vitamin B-Complex'],
        advice: 'Avoid spicy, sour and very hot food until it heals.\nDrink plenty of water.',
        followUpTreatment: 'Topical medication',
        followUpNotes: 'Review ulcer healing'
    },
    childCaries: {
        complaints: ['Tooth decay', 'Tooth pain'],
        complaintNote: 'Parent reports pain while eating sweets',
        findings: 'Carious primary molars 74 and 84. No swelling. Plaque present.',
        diagnosis: 'Early childhood caries i.r.t 74, 84',
        treatments: ['Dental Filling'],
        treatmentNote: 'GIC restorations done i.r.t 74, 84. Topical fluoride varnish applied.',
        notes: 'Diet counselling given to the parent.',
        medicines: [],
        advice: '',
        followUpTreatment: 'Fillings and fluoride varnish',
        followUpNotes: 'Three-month recall with fluoride varnish'
    },
    brokenTooth: {
        complaints: ['Broken tooth'],
        complaintNote: 'Front tooth chipped after a fall yesterday',
        findings: 'Enamel and dentin fracture i.r.t {t}. No mobility. Mild sensitivity to air.',
        diagnosis: 'Ellis Class II fracture i.r.t {t}',
        treatments: ['Dental Filling'],
        treatmentNote: 'Composite build-up done i.r.t {t}.',
        notes: 'Check pulp vitality after 4 weeks.',
        medicines: ['Paracetamol 650 mg'],
        advice: 'Avoid biting hard food with the front teeth for a week.\nCome back straight away if the tooth turns dark or painful.',
        followUpTreatment: 'Composite build-up i.r.t {t}',
        followUpNotes: 'Pulp vitality check'
    },
    wisdomPain: {
        complaints: ['Tooth pain', 'Swelling'],
        complaintNote: 'Pain behind the last tooth on the {side} side, difficulty opening the mouth',
        findings: 'Partially erupted {t} with inflamed gum flap. Mouth opening reduced to 30 mm.',
        diagnosis: 'Acute pericoronitis i.r.t {t}',
        treatments: ['Dental Checkup'],
        treatmentNote: 'Irrigation under the gum flap with povidone-iodine done. Extraction planned once the infection settles.',
        notes: 'OPG advised before extraction.',
        medicines: ['Amoxicillin 500 mg', 'Metronidazole 400 mg', 'Aceclofenac 100 mg + Paracetamol 325 mg', 'Povidone-Iodine 2% Gargle'],
        advice: 'Gargle with warm salt water 4 to 5 times a day.\nTake soft food and drink plenty of water.',
        followUpTreatment: 'Pericoronitis i.r.t {t}',
        followUpNotes: 'Review and plan extraction'
    }
}

const PATIENT_SEEDS: PatientSeed[] = [
    {
        name: 'Anjali Kumar',
        gender: 'female',
        dateOfBirth: '1992-06-18',
        phone: '9000010421',
        place: 'Panampilly Nagar',
        address: '14/220, Lotus Apartments, 5th Cross Road, Panampilly Nagar, Kochi',
        medicalHistory: 'No significant medical history',
        notes: 'Prefers morning appointments.',
        visits: [
            { day: -320, time: '11:00', case: 'scaling' },
            {
                day: -14,
                time: '10:30',
                case: 'rctStart',
                tooth: '36',
                followUp: { inDays: 14, completed: true, reminders: [{ day: -1, time: '18:10', channel: 'whatsapp' }] }
            },
            { day: 0, time: '10:30', case: 'rctComplete', tooth: '36', followUp: { inDays: 14 } }
        ]
    },
    {
        name: 'Thomas Kurian',
        gender: 'male',
        age: 71,
        phone: '9000010647',
        place: 'Aluva',
        address: 'Kurian Villa, Bank Junction, Aluva',
        medicalHistory: 'Diabetes, Hypertension',
        notes: 'Usually accompanied by his son Biju.',
        visits: [
            { day: -290, time: '10:00', case: 'dentureStart', followUp: { inDays: 7, completed: true } },
            { day: -283, time: '10:15', case: 'dentureDelivery', followUp: { inDays: 7, completed: true } },
            {
                day: -60,
                time: '11:30',
                case: 'dentureReview',
                followUp: {
                    inDays: 39,
                    reminders: [
                        { day: -18, time: '11:00', channel: 'whatsapp' },
                        { day: -9, time: '16:20', channel: 'call' }
                    ]
                }
            }
        ]
    },
    {
        name: 'Ayesha Siddiqui',
        gender: 'female',
        age: 33,
        phone: '9000010759',
        place: 'Mattancherry',
        medicalHistory: 'No significant medical history',
        allergies: 'NSAIDs',
        visits: [
            { day: -260, time: '16:40', case: 'scaling', followUp: { inDays: 180, completed: true } },
            { day: -80, time: '17:05', case: 'checkup' },
            { day: 0, time: '12:00', case: 'checkup', followUp: { inDays: 180 } }
        ]
    },
    {
        name: 'Sneha Thomas',
        gender: 'female',
        dateOfBirth: '2004-09-02',
        phone: '9000010862',
        place: 'Kaloor',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -200, time: '15:30', case: 'checkup' },
            { day: 0, time: '09:45', case: 'ulcer' }
        ]
    },
    {
        name: 'Rahul Menon',
        gender: 'male',
        age: 42,
        phone: '9000010974',
        place: 'Kakkanad',
        address: 'Flat 7B, Skyline Habitat, Kakkanad, Kochi',
        medicalHistory: 'Hypertension (on amlodipine)',
        visits: [
            {
                day: -190,
                time: '17:15',
                case: 'scaling',
                followUp: {
                    inDays: 180,
                    reminders: [
                        { day: -9, time: '10:05', channel: 'whatsapp' },
                        { day: -3, time: '16:40', channel: 'call' }
                    ]
                }
            }
        ]
    },
    {
        name: 'Vishnu Prasad',
        gender: 'male',
        dateOfBirth: '2007-01-23',
        phone: '9000011086',
        place: 'Aluva',
        medicalHistory: 'No significant medical history',
        notes: 'College student, available after 4 PM on weekdays.',
        visits: [
            { day: -170, time: '16:30', case: 'filling', tooth: '36' },
            { day: -100, time: '17:10', case: 'wisdomPain', tooth: '38', followUp: { inDays: 7, completed: true } },
            { day: -93, time: '16:45', case: 'extraction', tooth: '38', followUp: { inDays: 7, completed: true } }
        ]
    },
    {
        name: 'Mohammed Ashraf',
        gender: 'male',
        age: 38,
        phone: '9000011193',
        place: 'Kaloor',
        address: 'Door 32/1180, Stadium Link Road, Kaloor, Kochi',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -150, time: '18:00', case: 'checkup' },
            { day: -40, time: '11:00', case: 'rctStart', tooth: '46', followUp: { inDays: 7, completed: true } },
            { day: -33, time: '11:20', case: 'rctComplete', tooth: '46', followUp: { inDays: 7, completed: true } },
            {
                day: -26,
                time: '12:15',
                case: 'crownPrep',
                tooth: '46',
                corrected: true,
                followUp: {
                    inDays: 24,
                    notes: 'Crown trial and cementation (crown delayed at the lab)',
                    reminders: [{ day: -1, time: '12:30', channel: 'call' }]
                }
            }
        ]
    },
    {
        name: 'Nandini Menon',
        gender: 'female',
        age: 61,
        phone: '9000011208',
        place: 'Thrippunithura',
        medicalHistory: 'Diabetes (on metformin)',
        visits: [
            { day: -130, time: '10:45', case: 'periodontitis', followUp: { inDays: 14, completed: true } },
            { day: -116, time: '11:00', case: 'periodontitisUpper', followUp: { inDays: 90, completed: true, completedAfter: 2 } }
        ]
    },
    {
        name: 'Vivek Sharma',
        gender: 'male',
        age: 29,
        phone: '9000011317',
        place: 'Edappally',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -120, time: '18:20', case: 'checkup', followUp: { inDays: 180 } }
        ]
    },
    {
        name: 'Gayathri Devi',
        gender: 'female',
        age: 55,
        phone: '9000011425',
        place: 'Kadavanthra',
        medicalHistory: 'Thyroid disorder (on thyroxine)',
        visits: [
            { day: -110, time: '10:10', case: 'sensitivity', followUp: { inDays: 28, completed: true } },
            { day: -82, time: '10:30', case: 'filling', tooth: '24' }
        ]
    },
    {
        name: 'Suresh Babu',
        gender: 'male',
        age: 58,
        phone: '9000011532',
        place: 'Vyttila',
        address: 'Sree Nilayam, Ponnurunni, Vyttila, Kochi',
        medicalHistory: 'Hypertension',
        visits: [
            { day: -95, time: '12:00', case: 'extraction', tooth: '36', followUp: { inDays: 7, completed: true } },
            { day: -88, time: '12:20', case: 'extractionReview', tooth: '36' },
            {
                day: -10,
                time: '17:30',
                case: 'bridgePrep',
                tooth: '36',
                followUp: { inDays: 7, reminders: [{ day: -2, time: '09:40', channel: 'whatsapp' }] }
            }
        ]
    },
    {
        name: 'Abdul Kareem',
        gender: 'male',
        age: 64,
        phone: '9000011648',
        place: 'Fort Kochi',
        medicalHistory: 'Heart disease (on blood thinners)',
        notes: 'Check with his cardiologist before any extraction.',
        visits: [
            { day: -80, time: '11:40', case: 'brokenTooth', tooth: '11', followUp: { inDays: 28, completed: true } }
        ]
    },
    {
        name: 'Kiran George',
        gender: 'male',
        age: 47,
        phone: '9000011754',
        place: 'Palarivattom',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -70, time: '17:45', case: 'rctStart', tooth: '26', followUp: { inDays: 7, completed: true } },
            { day: -63, time: '18:00', case: 'rctComplete', tooth: '26', followUp: { inDays: 14, completed: true } },
            { day: -49, time: '17:50', case: 'crownPrep', tooth: '26', followUp: { inDays: 7, completed: true } },
            { day: -42, time: '18:10', case: 'crownFit', tooth: '26', followUp: { inDays: 180 } }
        ]
    },
    {
        name: 'Sanjay Patel',
        gender: 'male',
        age: 50,
        phone: '9000011869',
        place: 'Kakkanad',
        medicalHistory: 'Diabetes',
        visits: [
            { day: -65, time: '09:50', case: 'periodontitis', followUp: { inDays: 14, completed: true } },
            { day: -51, time: '10:05', case: 'periodontitisUpper' }
        ]
    },
    {
        name: 'Aditya Rao',
        gender: 'male',
        dateOfBirth: '2018-02-11',
        phone: '9000011973',
        place: 'Kalamassery',
        medicalHistory: 'No significant medical history',
        notes: 'Child patient. Accompanied by his mother, Smitha Rao.',
        visits: [
            { day: -45, time: '16:15', case: 'childCaries', followUp: { inDays: 90 } }
        ]
    },
    {
        name: 'Pooja Reddy',
        gender: 'female',
        age: 27,
        phone: '9000012086',
        place: 'Infopark, Kakkanad',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -40, time: '18:30', case: 'checkup' },
            { day: -4, time: '18:15', case: 'ulcer', followUp: { inDays: 7 } }
        ]
    },
    {
        name: 'Anitha Joseph',
        gender: 'female',
        age: 40,
        phone: '9000012194',
        place: 'Vypin',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -35, time: '15:40', case: 'filling', tooth: '16' },
            { day: -7, time: '10:20', case: 'brokenTooth', tooth: '21', followUp: { inDays: 7 } }
        ]
    },
    {
        name: 'Lakshmi Iyer',
        gender: 'female',
        age: 45,
        phone: '9000012301',
        place: 'Ernakulam North',
        medicalHistory: 'Asthma (uses an inhaler)',
        allergies: 'Aspirin',
        visits: [
            {
                day: -28,
                time: '11:10',
                case: 'sensitivity',
                followUp: { inDays: 28, reminders: [{ day: -1, time: '17:20', channel: 'whatsapp' }] }
            }
        ]
    },
    {
        name: 'Meera Pillai',
        gender: 'female',
        age: 52,
        phone: '9000012418',
        place: 'Kaloor',
        medicalHistory: 'Diabetes',
        visits: [
            {
                day: -20,
                time: '12:30',
                case: 'periodontitis',
                followUp: { inDays: 14, reminders: [{ day: -4, time: '11:15', channel: 'call' }] }
            }
        ]
    },
    {
        name: 'Priya Varghese',
        gender: 'female',
        age: 25,
        phone: '9000012523',
        place: 'Panampilly Nagar',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -14, time: '17:00', case: 'whitening', followUp: { inDays: 14 } }
        ]
    },
    {
        name: 'Nisha Abraham',
        gender: 'female',
        age: 44,
        phone: '9000012637',
        place: 'Angamaly',
        medicalHistory: 'No significant medical history',
        allergies: 'Sulfa drugs',
        visits: [
            { day: -58, time: '16:00', case: 'whitening', followUp: { inDays: 14, completed: true } },
            { day: -44, time: '16:20', case: 'whiteningFinal' }
        ]
    },
    {
        name: 'Joseph Mathew',
        gender: 'male',
        age: 67,
        phone: '9000012742',
        place: 'Perumbavoor',
        medicalHistory: 'Hypertension, Diabetes',
        notes: 'Hard of hearing. Speak slowly and clearly.',
        visits: [
            { day: -9, time: '11:30', case: 'dentureStart', followUp: { inDays: 9 } }
        ]
    },
    {
        name: 'Divya Krishnan',
        gender: 'female',
        age: 36,
        phone: '9000012856',
        place: 'Edappally',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -6, time: '15:15', case: 'rctStart', tooth: '47', followUp: { inDays: 7 } }
        ]
    },
    {
        name: 'Reshma Nair',
        gender: 'female',
        age: 30,
        phone: '9000012961',
        place: 'Kadavanthra',
        medicalHistory: 'No significant medical history',
        allergies: 'Penicillin',
        visits: [
            {
                day: -5,
                time: '19:00',
                case: 'wisdomPain',
                tooth: '38',
                medicines: ['Azithromycin 500 mg', 'Metronidazole 400 mg', 'Paracetamol 650 mg', 'Povidone-Iodine 2% Gargle'],
                followUp: { inDays: 7 }
            }
        ]
    },
    {
        name: 'Fathima Rahman',
        gender: 'female',
        age: 28,
        phone: '9000013075',
        place: 'Kaloor',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: -3, time: '10:40', case: 'extraction', tooth: '48', followUp: { inDays: 7 } }
        ]
    },
    {
        name: 'Rohit Gupta',
        gender: 'male',
        age: 26,
        phone: '9000013182',
        place: 'Vyttila',
        notes: 'Registered at the front desk. Consultation pending.',
        registered: { day: -1, time: '17:40' },
        visits: []
    },
    {
        name: 'Arjun Nair',
        gender: 'male',
        age: 31,
        phone: '9000013296',
        place: 'Kakkanad',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: 0, time: '11:15', case: 'filling', tooth: '26' }
        ]
    },
    {
        name: 'Harish Chandran',
        gender: 'male',
        age: 35,
        phone: '9000013305',
        place: 'Kalamassery',
        medicalHistory: 'No significant medical history',
        visits: [
            { day: 0, time: '12:40', case: 'extraction', tooth: '18', followUp: { inDays: 7 } }
        ]
    }
]

const SIDES: Record<string, string> = {
    '1': 'upper right',
    '2': 'upper left',
    '3': 'lower left',
    '4': 'lower right',
    '5': 'upper right',
    '6': 'upper left',
    '7': 'lower left',
    '8': 'lower right'
}

const pad = (value: number): string => String(value).padStart(2, '0')

const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)

    return hours * 60 + minutes
}

const fromMinutes = (value: number): string => `${pad(Math.floor(value / 60))}:${pad(value % 60)}`

const shiftMinutes = (iso: string, minutes: number): string => new Date(new Date(iso).getTime() + minutes * 60_000).toISOString()

const fillCase = (text: string, tooth?: string): string => {
    if (!tooth) return text

    return text.replace(/\{t\}/g, tooth).replace(/\{side\}/g, SIDES[tooth.charAt(0)] ?? '')
}

const findMedicine = (name: string): Omit<Medicine, 'id'> => {
    const preset = COMMON_MEDICINES.find(item => item.name === name)

    return preset ? { ...preset } : { name, dosage: '', frequency: '', duration: '', instructions: '' }
}

interface ResolvedVisit {
    day: number
    time: string
}

/**
 * Sample visits planned for today must never be in the future. Visits planned
 * for later today are moved to earlier slots; early in the morning, when there
 * is no room left today, they move to the same time yesterday.
 */
const resolveTodayVisits = (nowMinutes: number): Map<string, ResolvedVisit> => {
    const entries: Array<{ key: string, minutes: number, time: string }> = []

    PATIENT_SEEDS.forEach((seed, patientIndex) => {
        seed.visits.forEach((visit, visitIndex) => {
            if (visit.day === 0) entries.push({ key: `${patientIndex}-${visitIndex}`, minutes: toMinutes(visit.time), time: visit.time })
        })
    })

    const late = entries
        .filter(entry => entry.minutes > nowMinutes - 20)
        .sort((a, b) => b.minutes - a.minutes)

    const resolved = new Map<string, ResolvedVisit>()

    late.forEach((entry, order) => {
        const fallback = nowMinutes - 20 - order * 25

        if (fallback >= 15) resolved.set(entry.key, { day: 0, time: fromMinutes(fallback) })
        else resolved.set(entry.key, { day: -1, time: entry.time })
    })

    return resolved
}

const cloneSettings = () => JSON.parse(JSON.stringify(DEFAULT_SETTINGS)) as ClinicData['settings']

export const createEmptyData = (): ClinicData => ({
    patients: [],
    consultations: [],
    prescriptions: [],
    followUps: [],
    reminders: [],
    settings: cloneSettings(),
    counters: { patient: 0, prescription: 0 }
})

export const createSeedData = (now: Date = new Date()): ClinicData => {
    const today = toDayKey(now)
    const latestAllowed = new Date(now.getTime() - 5 * 60_000).toISOString()
    const todayVisits = resolveTodayVisits(now.getHours() * 60 + now.getMinutes())

    const at = (day: number, time: string): string => combineDateTime(addDays(today, day), time)
    const clampPast = (iso: string): string => (iso > latestAllowed ? latestAllowed : iso)

    const drafts = PATIENT_SEEDS.map((seed, patientIndex) => {
        const visitAt = (visitIndex: number): string => {
            const visit = seed.visits[visitIndex]
            const resolved = todayVisits.get(`${patientIndex}-${visitIndex}`)

            return resolved ? at(resolved.day, resolved.time) : at(visit.day, visit.time)
        }

        const registeredAt = seed.registered
            ? at(seed.registered.day, seed.registered.time)
            : shiftMinutes(visitAt(0), -10)

        return { seed, registeredAt, visitAt }
    })

    drafts.sort((a, b) => a.registeredAt.localeCompare(b.registeredAt))

    const patients: Patient[] = []
    const consultations: Consultation[] = []
    const prescriptionDrafts: Prescription[] = []
    const followUps: FollowUp[] = []
    const reminders: ReminderActivity[] = []

    drafts.forEach((draft, order) => {
        const { seed, registeredAt, visitAt } = draft
        const patientId = `seed-patient-${order + 1}`

        const patient: Patient = {
            id: patientId,
            code: formatPatientCode(order + 1),
            name: seed.name,
            phone: seed.phone,
            age: seed.dateOfBirth ? null : seed.age ?? null,
            dateOfBirth: seed.dateOfBirth ?? null,
            gender: seed.gender,
            place: seed.place,
            address: seed.address ?? '',
            medicalHistory: seed.medicalHistory ?? '',
            allergies: seed.allergies ?? '',
            notes: seed.notes ?? '',
            createdAt: registeredAt,
            updatedAt: registeredAt,
            history: [createAudit('created', 'Patient registered', registeredAt)]
        }

        patients.push(patient)

        seed.visits.forEach((visit, visitIndex) => {
            const template = CASES[visit.case]
            const fill = (text: string): string => fillCase(text, visit.tooth)
            const consultedAt = visitAt(visitIndex)
            const consultationId = `seed-consultation-${order + 1}-${visitIndex + 1}`

            const consultation: Consultation = {
                id: consultationId,
                patientId,
                consultedAt,
                complaints: [...template.complaints],
                complaintNote: fill(template.complaintNote),
                findings: fill(template.findings),
                diagnosis: fill(template.diagnosis),
                treatments: [...template.treatments],
                treatmentNote: fill(template.treatmentNote),
                notes: fill(template.notes),
                createdAt: consultedAt,
                updatedAt: consultedAt,
                history: [createAudit('created', 'Consultation recorded', consultedAt)]
            }

            if (visit.corrected) {
                const wrongDate = shiftMinutes(consultedAt, 24 * 60 - 45)
                const correctedAt = clampPast(shiftMinutes(consultedAt, 24 * 60 + 30))

                consultation.createdAt = wrongDate
                consultation.updatedAt = correctedAt
                consultation.history = [
                    createAudit('created', 'Consultation recorded', wrongDate),
                    createAudit('date-corrected', `Consultation date corrected from ${formatDateTime(wrongDate)} to ${formatDateTime(consultedAt)}. Reason: Entered the next day, corrected to the actual visit time`, correctedAt)
                ]
            }

            consultations.push(consultation)

            const medicineNames = visit.medicines ?? template.medicines

            if (medicineNames.length > 0) {
                const prescribedAt = shiftMinutes(consultedAt, 14)

                prescriptionDrafts.push({
                    id: `seed-prescription-${order + 1}-${visitIndex + 1}`,
                    number: '',
                    patientId,
                    consultationId,
                    prescribedAt,
                    complaint: describeComplaint(consultation.complaints, consultation.complaintNote),
                    diagnosis: consultation.diagnosis,
                    medicines: medicineNames.map((name, medicineIndex) => ({
                        id: `${consultationId}-medicine-${medicineIndex + 1}`,
                        ...findMedicine(name)
                    })),
                    advice: template.advice || DEFAULT_SETTINGS.defaultAdvice,
                    notes: '',
                    createdAt: prescribedAt,
                    updatedAt: prescribedAt,
                    history: [createAudit('created', 'Prescription created', prescribedAt)]
                })
            }

            if (!visit.followUp) return

            const followUpSeed = visit.followUp
            const followUpId = `seed-follow-up-${order + 1}-${visitIndex + 1}`
            const createdAt = shiftMinutes(consultation.createdAt, medicineNames.length > 0 ? 18 : 12)
            const dueDay = visit.day + followUpSeed.inDays
            const dueDate = addDays(today, dueDay)
            const treatment = fill(template.followUpTreatment)

            const followUp: FollowUp = {
                id: followUpId,
                patientId,
                consultationId,
                treatment,
                dueDate,
                notes: followUpSeed.notes ?? fill(template.followUpNotes),
                status: 'pending',
                completedAt: null,
                createdAt,
                updatedAt: createdAt,
                history: [createAudit('created', `Follow-up scheduled for ${formatDate(dueDate)}`, createdAt)]
            }

            if (followUpSeed.completed) {
                const completionDay = dueDay + (followUpSeed.completedAfter ?? 0)
                const visitIndexOnDay = seed.visits.findIndex(item => item.day === completionDay)
                const completionTime = visitIndexOnDay === -1
                    ? at(completionDay, '11:00')
                    : shiftMinutes(visitAt(visitIndexOnDay), -3)
                const completedAt = clampPast(completionTime)

                followUp.status = 'completed'
                followUp.completedAt = completedAt
                followUp.updatedAt = completedAt
                followUp.history = [...followUp.history, createAudit('completed', 'Marked as completed', completedAt)]
            }

            followUps.push(followUp)

            followUpSeed.reminders?.forEach((reminder, reminderIndex) => {
                const reminderAt = clampPast(at(reminder.day, reminder.time))
                const reminderDay = addDays(today, reminder.day)
                const templateId = dueDate < reminderDay ? 'tpl-missed' : dueDate === reminderDay ? 'tpl-due-today' : 'tpl-follow-up'
                const template = DEFAULT_SETTINGS.templates.find(item => item.id === templateId) ?? DEFAULT_SETTINGS.templates[0]

                reminders.push({
                    id: `${followUpId}-reminder-${reminderIndex + 1}`,
                    patientId,
                    followUpId,
                    channel: reminder.channel,
                    message: reminder.channel === 'whatsapp'
                        ? fillTemplate(template.body, {
                            patientName: seed.name,
                            clinicName: DEFAULT_SETTINGS.clinicName,
                            doctorName: DEFAULT_SETTINGS.doctorName,
                            followUpDate: formatDate(dueDate),
                            treatment,
                            clinicPhone: DEFAULT_SETTINGS.phone
                        })
                        : '',
                    at: reminderAt
                })
            })
        })
    })

    const prescriptions = prescriptionDrafts
        .sort((a, b) => a.prescribedAt.localeCompare(b.prescribedAt))
        .map((prescription, index) => ({ ...prescription, number: formatPrescriptionNumber(index + 1) }))

    return {
        patients,
        consultations,
        prescriptions,
        followUps,
        reminders,
        settings: cloneSettings(),
        counters: { patient: patients.length, prescription: prescriptions.length }
    }
}
